import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { Settings } from 'lucide-react';

import { AppShell } from 'components/dashboard/app-shell';
import { useUser } from 'contexts/UserContext';

import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

import { listNotes, type NoteListItem } from '../services/notes';

function pickErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (data?.detail) return String(data.detail);
  if (err?.message) return String(err.message);
  return fallback;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  try {
    return format(d, 'dd/MM/yyyy HH:mm', { locale: itLocale });
  } catch {
    return d.toLocaleString();
  }
}

export default function NotesPage() {
  const { user, loading: userLoading } = useUser();
  const navigate = useNavigate();

  const [notes, setNotes] = useState<NoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listNotes();
      // Trust-the-server sull'ordine: il backend ordina per created_at desc.
      const data = res.data;
      if (Array.isArray(data)) {
        setNotes(data);
      } else if (data && Array.isArray((data as any).results)) {
        setNotes((data as any).results);
      } else {
        setNotes([]);
      }
    } catch (err: any) {
      setError(pickErrorMessage(err, 'Impossibile caricare le note.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Caricamento...</div>
      </div>
    );
  }

  if (!user && !localStorage.getItem('accessToken')) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppShell currentUser={user}>
      <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Note</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Note e meeting raccolte dal tuo bot Telegram.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings/telegram">
              <Settings />
              Configura bot
            </Link>
          </Button>
        </header>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Errore</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : loading ? (
          <NotesTableSkeleton />
        ) : notes.length === 0 ? (
          <EmptyState />
        ) : (
          <NotesTable
            notes={notes}
            onRowClick={(id) => navigate(`/notes/${id}`)}
          />
        )}
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed p-10 text-center">
      <p className="text-sm text-muted-foreground">
        Nessuna nota ancora. Manda{' '}
        <code className="bg-muted rounded px-1 py-0.5">/nuova</code> al tuo bot
        Telegram per crearne una.
      </p>
      <p className="text-sm text-muted-foreground mt-2">
        Non hai ancora un bot collegato?
      </p>
      <Button asChild variant="outline" size="sm" className="mt-3">
        <Link to="/settings/telegram">
          <Settings />
          Configura bot Telegram
        </Link>
      </Button>
    </div>
  );
}

function NotesTableSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-9 w-full" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

interface NotesTableProps {
  notes: NoteListItem[];
  onRowClick: (id: number | string) => void;
}

function NotesTable({ notes, onRowClick }: NotesTableProps) {
  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Titolo</TableHead>
            <TableHead className="w-[140px]">Tipo</TableHead>
            <TableHead className="w-[180px]">Data</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {notes.map((n) => (
            <TableRow
              key={n.id}
              className="cursor-pointer"
              onClick={() => onRowClick(n.id)}
            >
              <TableCell className="font-medium">
                <span className="text-primary hover:underline">
                  {n.title || '(senza titolo)'}
                </span>
              </TableCell>
              <TableCell>
                <Badge variant={n.type === 'meeting' ? 'default' : 'secondary'}>
                  {n.type === 'meeting' ? 'meeting' : 'note'}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDate(n.created_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
